import { createSelector, Selector } from '@reduxjs/toolkit'
import { ColDef, GridOptions, RowClassParams, ValueFormatterParams } from 'ag-grid-community'
import { InflightNegotiations, Negotiation } from 'api'
import { Locale, Translation } from 'locale'
import { GlobalState } from 'store'
import { counterpartyColumnDetailSelector } from './selectors'
import {
  dateFilterParams,
  gridOptionsBase,
  groupingRowColDef,
  TypedEditableCallbackParams,
  TypedValueGetterParams,
  ValidationCellProps,
  withValidation
} from 'view/components/datagrid'
import {
  dateQuickFilterTextGetter,
  getHaircutFormatOptions,
  getOwnDirection,
  getRateFormatOptions,
  makeCusipGetter,
  makeDateFormatter,
  makeHaircutFormatter,
  makeIsinGetter,
  makeNotionalFormatter,
  makeRateFormatter,
  notionalHeaderValueGetter
} from 'view/shared/datagrid'
import { getNegotiatedValue } from 'model'
import { isNil } from 'lodash-es'
import { aggregateAllOrNothing, hasMultipleValues } from 'utils'
import { BASE_DIRECTION, BASE_ISSUE } from 'view/shared/datagrid/baseColumns'
import { NumericInput } from 'view/components/inputs'
import { PivotedRowGroupedColumnHeaderWithoutExpandedComponent } from '../LiveNegotiationsGrid/PivotedRowGroupedColumnHeaderRenderer'
import { InitiatedGridViewActionsButton, InitiatedGridViewHeaderActionsButton } from './InitiatedGridViewActionsButton'
import { TextOncePerCollateral } from './TextOncePerCollateral'
import {
  aggregatedPartialDetailRowDataGetter,
  AggregatedPartialNegotiationDetails,
  canUpdateGridViewHaircut,
  canUpdateGridViewRate,
  encodeColIdValue,
  getCounterpartyNegotiations,
  getStatusValidationIssues,
  inflightNegotiationsRowDataGetter
} from './utils'
import { CellContentWithNegotiationsInfo } from './CellContentWithNegotiationsInfo'
import { NegotiationFieldNames } from '../LiveNegotiationsGrid/LiveGridColumnNames'
import { getRateModificationType } from '../shared/deltaGetters'
import { FontWeight } from 'view/styles/constants'
import { InitiatedGridViewId } from './InitiatedGridViewColumnNames'
import { getNegotiationsBreakdown } from './getNegotiationsBreakdown'
import { NegotiableCell } from '../LiveNegotiationsGrid/Negotiable'
import { PinnedCollateralIcon } from './PinnedCollateralIcon'

export const initiatedGridViewColumnDefsSelector: Selector<GlobalState, ColDef[]> = createSelector(
  [counterpartyColumnDetailSelector],
  counterparties => [
    {
      ...groupingRowColDef,
      headerName: Locale.translate(Translation.TC_GROUPING),
      width: 180,
      minWidth: 130,
      headerComponent: undefined,
      pinned: false,
      resizable: false,
      hide: false
    },
    {
      colId: InitiatedGridViewId.COLLATERAL_PINNED,
      headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
      cellRenderer: PinnedCollateralIcon,
      resizable: false,
      suppressMovable: true,
      width: 15,
      maxWidth: 15,
      minWidth: 0
    },
    {
      colId: InitiatedGridViewId.COLLATERAL_NAME,
      valueGetter: (params: TypedValueGetterParams<InflightNegotiations>) => params.data && params.data.collateral.name,
      headerName: Locale.translate(Translation.TC_COLLATERAL),
      cellRenderer: TextOncePerCollateral,
      cellRendererParams: {
        fontWeight: FontWeight.Bold
      },
      width: 150,
      filter: 'agSetColumnFilter'
    },
    {
      colId: InitiatedGridViewId.COLLATERAL_ISIN_OR_BUI,
      valueGetter: makeIsinGetter(),
      headerName: Locale.translate(Translation.TC_ISIN_BUI),
      cellRenderer: TextOncePerCollateral,
      cellRendererParams: {
        fontWeight: FontWeight.Bold
      },
      width: 150,
      hide: true
    },
    {
      colId: InitiatedGridViewId.COLLATERAL_CUSIP,
      valueGetter: makeCusipGetter(),
      headerName: Locale.translate(Translation.TC_CUSIP),
      cellRenderer: TextOncePerCollateral,
      cellRendererParams: {
        fontWeight: FontWeight.Bold
      },
      width: 120,
      hide: true
    },
    {
      ...BASE_DIRECTION,
      colId: InitiatedGridViewId.NEGOTIATION_DIRECTION,
      cellRendererParams: {
        dataTestId: 'GridView-Direction-Cell'
      },
      valueGetter: (params: TypedValueGetterParams<InflightNegotiations>) => {
        const direction = params.data?.direction
        return direction && getOwnDirection(direction, true)
      }
    },
    {
      ...BASE_ISSUE,
      colId: InitiatedGridViewId.COLLATERAL_ISSUE,
      cellRenderer: TextOncePerCollateral,
      valueGetter: (params: TypedValueGetterParams<InflightNegotiations>) => {
        return params.data?.collateral?.issue
      }
    },
    {
      colId: InitiatedGridViewId.COLLATERAL_COUPON_MATURE_DATE,
      valueGetter: (params: TypedValueGetterParams<InflightNegotiations>) => params.data?.collateral.couponMaturityDate,
      cellRenderer: TextOncePerCollateral,
      headerName: Locale.translate(Translation.TC_MATURITY_DATE),
      width: 100,
      filter: 'agDateColumnFilter',
      ...dateFilterParams,
      valueFormatter: makeDateFormatter(),
      getQuickFilterText: dateQuickFilterTextGetter,
      hide: true
    },
    {
      colId: InitiatedGridViewId.TARGET,
      headerName: Locale.translate(Translation.TC_TARGET),
      valueGetter: (params: TypedValueGetterParams<InflightNegotiations>) => params?.data?.target,
      editable: ({ data }: TypedEditableCallbackParams<InflightNegotiations>) => !!data?.direction && !!data?.collateral.id,
      width: 200,
      valueFormatter: (params: ValueFormatterParams) => {
        if (isNil(params.value)) return ''
        return makeNotionalFormatter()(params)
      },
      cellEditorParams: {
        inputComponent: NumericInput,
        inputParams: () => ({
          dataTestId: 'GridView-Target-NumericInput'
        }),
        updateField: 'target'
      }
    },
    {
      colId: InitiatedGridViewId.INFLIGHT,
      headerName: Locale.translate(Translation.TC_INFLIGHT),
      valueGetter: (params: TypedValueGetterParams<InflightNegotiations>) => params?.data?.inflight,
      valueFormatter: (params: ValueFormatterParams) => {
        if (isNil(params.value)) return ''
        return makeNotionalFormatter()(params)
      }
    },
    ...counterparties.map(c => ({
      headerName: c.headerName,
      headerGroupComponent: PivotedRowGroupedColumnHeaderWithoutExpandedComponent,
      headerGroupComponentParams: {
        counterpartyId: c.id
      },
      marryChildren: true,
      children: [
        {
          cellClass: 'border-left-row',
          headerClass: 'border-left-row',
          headerComponent: InitiatedGridViewHeaderActionsButton,
          headerComponentParams: {
            'data-test-id': `CounterpartyHeader-${c.headerName}`
          },
          width: 85,
          colId: encodeColIdValue(InitiatedGridViewId.COUNTERPARTY_ACTIONS, c.id),
          headerName: Locale.translate(Translation.TC_ACTIONS),
          cellRenderer: withValidation(InitiatedGridViewActionsButton),
          cellRendererParams: {
            centered: true,
            dataTestId: `GridViewActions-Counterparty-${c.headerName}`,
            getValidationIssues: getStatusValidationIssues
          }
        },
        {
          colId: encodeColIdValue(InitiatedGridViewId.NEGOTIATION_STATUS, c.id),
          headerName: Locale.translate(Translation.TC_STATUS),
          hide: true,
          valueGetter: (params: TypedValueGetterParams<InflightNegotiations>) => {
            if (params.node.group) return undefined
            const negotiations = inflightNegotiationsRowDataGetter(params) ?? []
            const status = aggregateAllOrNothing(negotiations, n => n.negotiation.status)
            return status
          }
        },
        {
          colId: encodeColIdValue(InitiatedGridViewId.COUNTERPARTY_NOTIONAL, c.id),
          headerValueGetter: notionalHeaderValueGetter,
          headerName: Locale.translate(Translation.TC_NOTIONAL_SHORT),
          width: 200,
          valueFormatter: (params: ValueFormatterParams<InflightNegotiations, AggregatedPartialNegotiationDetails>) => {
            const notional = params?.value?.negotiation?.notional

            if (isNil(notional)) return ''

            return makeNotionalFormatter()({ ...params, data: params.value, value: notional })
          },
          cellRenderer: CellContentWithNegotiationsInfo,
          valueGetter: aggregatedPartialDetailRowDataGetter
        },
        {
          colId: encodeColIdValue(InitiatedGridViewId.COUNTERPARTY_RATE, c.id),
          headerName: Locale.translate(Translation.TC_RATE),
          cellEditorParams: {
            inputComponent: NumericInput,
            getNodeId: () => encodeColIdValue(InitiatedGridViewId.COUNTERPARTY_RATE, c.id),
            inputParams: (detail: any) => ({
              formatOptions: getRateFormatOptions(detail.referenceRate),
              dataTestId: `GridView-${c.headerName}-Rate-NumericInput`
            }),
            rowDataGetter: aggregatedPartialDetailRowDataGetter,
            defaultField: NegotiationFieldNames.RATE_NEGOTIATED,
            isModifiedField: NegotiationFieldNames.RATE_IS_MODIFIED,
            updateField: NegotiationFieldNames.RATE_MODIFIED,
            validationField: NegotiationFieldNames.RATE
          },
          cellRenderer: NegotiableCell,
          cellRendererParams: {
            getTooltipWhileNoValidations: (params: ValidationCellProps<InflightNegotiations>) => {
              const negotiations = inflightNegotiationsRowDataGetter(params as any) ?? []
              return getNegotiationsBreakdown(negotiations, params.context.userPreference)
            },
            getModificationType: ({ negotiation: { rate } }: { negotiation: Partial<Negotiation> }) => getRateModificationType(rate),
            isRateField: true,
            rowDataGetter: aggregatedPartialDetailRowDataGetter,
            validationField: NegotiationFieldNames.RATE
          },
          valueFormatter: (params: ValueFormatterParams<InflightNegotiations, AggregatedPartialNegotiationDetails>) => {
            const negotiations = inflightNegotiationsRowDataGetter(params as any) ?? []

            if (hasMultipleValues(negotiations, n => getNegotiatedValue(n.negotiation.rate))) {
              return Locale.translate(Translation.SC_MULTIPLE)
            }

            const rate = getNegotiatedValue(params?.value?.negotiation?.rate)

            if (isNil(rate)) return ''

            return makeRateFormatter()({ ...params, data: params.value, value: rate })
          },
          valueGetter: aggregatedPartialDetailRowDataGetter,
          editable: ({ data }: TypedEditableCallbackParams<InflightNegotiations>) => {
            const negotiations = getCounterpartyNegotiations(data, c.id)
            return canUpdateGridViewRate(negotiations)
          }
        },
        {
          colId: encodeColIdValue(InitiatedGridViewId.COUNTERPARTY_HAIRCUT, c.id),
          headerName: Locale.translate(Translation.TC_HAIRCUT),
          cellEditorParams: {
            inputComponent: NumericInput,
            getNodeId: () => encodeColIdValue(InitiatedGridViewId.COUNTERPARTY_HAIRCUT, c.id),
            inputParams: (detail: any, context: any) => ({
              formatOptions: getHaircutFormatOptions(undefined, context.userPreference.haircutSign, detail.ownOrder, detail.direction),
              dataTestId: `GridView-${c.headerName}-Haircut-NumericInput`
            }),
            rowDataGetter: aggregatedPartialDetailRowDataGetter,
            defaultField: NegotiationFieldNames.HAIRCUT_NEGOTIATED,
            isModifiedField: NegotiationFieldNames.HAIRCUT_IS_MODIFIED,
            updateField: NegotiationFieldNames.HAIRCUT_MODIFIED,
            validationField: NegotiationFieldNames.HAIRCUT
          },
          valueGetter: aggregatedPartialDetailRowDataGetter,
          cellRenderer: NegotiableCell,
          cellRendererParams: {
            getTooltipWhileNoValidations: (params: ValidationCellProps<InflightNegotiations>) => {
              const negotiations = inflightNegotiationsRowDataGetter(params as any) ?? []
              return getNegotiationsBreakdown(negotiations, params.context.userPreference)
            },
            getModificationType: ({ negotiation: { haircut } }: { negotiation: Partial<Negotiation> }) => getRateModificationType(haircut),
            isHaircutOrHaircutTypeField: true,
            rowDataGetter: aggregatedPartialDetailRowDataGetter,
            validationField: NegotiationFieldNames.HAIRCUT
          },
          valueFormatter: (params: ValueFormatterParams<InflightNegotiations, AggregatedPartialNegotiationDetails>) => {
            const negotiations = inflightNegotiationsRowDataGetter(params as any) ?? []

            if (hasMultipleValues(negotiations, n => getNegotiatedValue(n.negotiation.haircut))) {
              return Locale.translate(Translation.SC_MULTIPLE)
            }

            const haircut = getNegotiatedValue(params?.value?.negotiation?.haircut)

            if (isNil(haircut)) return ''

            return makeHaircutFormatter()({ ...params, data: params.value, value: haircut })
          },
          editable: ({ data }: TypedEditableCallbackParams<InflightNegotiations>) => {
            const negotiations = getCounterpartyNegotiations(data, c.id)
            return canUpdateGridViewHaircut(negotiations)
          }
        }
      ],
      headerClass: 'header-cell--grouped'
    }))
  ]
)

export const initiatedGridViewGridOptions: GridOptions = {
  ...gridOptionsBase,
  suppressContextMenu: false,
  getRowId: ({ data }: { data: InflightNegotiations }) => data.id,
  getRowClass: (params: RowClassParams<InflightNegotiations>) => {
    const nextRow = params.api.getDisplayedRowAtIndex(params.rowIndex + 1)
    if (nextRow?.data?.collateral.id !== params?.node?.data?.collateral?.id) {
      return 'border-bottom-row'
    }

    return ''
  }
}
